"use client";

import { usePostHog } from "@posthog/react";
import { useQueryClient } from "@tanstack/react-query";
import { parseError } from "evlog";
import { useCallback } from "react";
import type { z } from "zod";

import { updateGameplaySettingInputSchema } from "#/lib/playerSettingsSchemas";
import { patchMyPlayerProfileQuery } from "#/lib/queries";
import { updateMyGameplaySetting } from "#/server/updateMyGameplaySetting";

import { ProfileToggleSetting } from "./-profile.toggleSetting";
import type { ProfileUserId } from "./-profile.types";

type GameplaySetting = z.infer<typeof updateGameplaySettingInputSchema>["setting"];

export function ProfileBooleanSetting({
  userId,
  setting,
  title,
  description,
  initialEnabled,
}: {
  userId: ProfileUserId;
  setting: GameplaySetting;
  title: string;
  description: string;
  initialEnabled: boolean;
}) {
  const posthog = usePostHog();
  const queryClient = useQueryClient();

  const persist = useCallback(
    async (enabled: boolean) => {
      try {
        await updateMyGameplaySetting({ data: { setting, enabled } });
        patchMyPlayerProfileQuery({ queryClient, userId, patch: { [setting]: enabled } });
      } catch (err) {
        const parsed = parseError(err);
        if (parsed.status >= 500) posthog.captureException(err);
        throw err;
      }
    },
    [posthog, queryClient, setting, userId],
  );

  return (
    <ProfileToggleSetting
      description={description}
      initialEnabled={initialEnabled}
      persist={persist}
      switchId={`profile-${setting}`}
      title={title}
    />
  );
}
